import { Card } from 'primereact/card';

export const DesignTokensSection = () => {
  const colors = [
    {
      name: 'Primary',
      cssVar: '--color-primary',
    },
    {
      name: 'Primary Dark',
      cssVar: '--color-primary-dark',
    },
    {
      name: 'Primary Light',
      cssVar: '--color-primary-light',
    },
  ];

  const semanticColors = [
    {
      name: 'Blue 600',
      cssVar: '--color-blue-600',
    },
    {
      name: 'Green 600',
      cssVar: '--color-green-600',
    },
    {
      name: 'Red 600',
      cssVar: '--color-red-600',
    },
    {
      name: 'Amber 600',
      cssVar: '--color-amber-600',
    },
    {
      name: 'Purple 600',
      cssVar: '--color-purple-600',
    },
    {
      name: 'Cyan 600',
      cssVar: '--color-cyan-600',
    },
  ];

  const grayScale = [
    {
      name: 'Gray 50',
      cssVar: '--color-gray-50',
    },
    {
      name: 'Gray 100',
      cssVar: '--color-gray-100',
    },
    {
      name: 'Gray 200',
      cssVar: '--color-gray-200',
    },
    {
      name: 'Gray 300',
      cssVar: '--color-gray-300',
    },
    {
      name: 'Gray 400',
      cssVar: '--color-gray-400',
    },
    {
      name: 'Gray 500',
      cssVar: '--color-gray-500',
    },
    {
      name: 'Gray 600',
      cssVar: '--color-gray-600',
    },
    {
      name: 'Gray 700',
      cssVar: '--color-gray-700',
    },
    {
      name: 'Gray 800',
      cssVar: '--color-gray-800',
    },
    {
      name: 'Gray 900',
      cssVar: '--color-gray-900',
    },
  ];

  const spacing = [
    { name: 'XS', value: '4px', class: 'w-[--spacing-xs] h-[--spacing-xs]' },
    { name: 'SM', value: '8px', class: 'w-[--spacing-sm] h-[--spacing-sm]' },
    { name: 'MD', value: '12px', class: 'w-[--spacing-md] h-[--spacing-md]' },
    { name: 'LG', value: '16px', class: 'w-[--spacing-lg] h-[--spacing-lg]' },
    { name: 'XL', value: '24px', class: 'w-[--spacing-xl] h-[--spacing-xl]' },
    {
      name: '2XL',
      value: '32px',
      class: 'w-[--spacing-2xl] h-[--spacing-2xl]',
    },
    {
      name: '3XL',
      value: '48px',
      class: 'w-[--spacing-3xl] h-[--spacing-3xl]',
    },
  ];

  const borderRadius = [
    { name: 'Small', cssVar: '--radius-sm' },
    { name: 'Medium', cssVar: '--radius-md' },
    { name: 'Large', cssVar: '--radius-lg' },
    { name: 'Full', cssVar: '--radius-full' },
  ];

  const shadows = [
    { name: 'Small', class: 'shadow-sm' },
    { name: 'Medium', class: 'shadow-md' },
    { name: 'Large', class: 'shadow-lg' },
    { name: 'X-Large', class: 'shadow-xl' },
  ];

  const commonIcons = [
    'pi-home',
    'pi-user',
    'pi-users',
    'pi-cog',
    'pi-search',
    'pi-filter',
    'pi-plus',
    'pi-minus',
    'pi-times',
    'pi-check',
    'pi-chevron-left',
    'pi-chevron-right',
    'pi-chevron-up',
    'pi-chevron-down',
    'pi-calendar',
    'pi-clock',
    'pi-envelope',
    'pi-phone',
    'pi-pencil',
    'pi-trash',
    'pi-save',
    'pi-download',
    'pi-upload',
    'pi-file',
    'pi-folder',
    'pi-chart-bar',
    'pi-chart-line',
    'pi-chart-pie',
    'pi-eye',
    'pi-eye-slash',
    'pi-lock',
    'pi-unlock',
    'pi-star',
    'pi-heart',
    'pi-thumbs-up',
    'pi-comment',
  ];

  return (
    <div id='design-tokens' className='space-y-6'>
      <div>
        <h2 className='text-2xl font-bold mb-2'>Design Tokens & Foundations</h2>
        <p className='text-gray-600 mb-4'>
          Core design system variables used throughout the application.
        </p>
      </div>

      {/* Color Palette */}
      <Card title='Color Palette' className='mb-4'>
        <div className='space-y-6'>
          {/* Primary Colors */}
          <div>
            <h3 className='text-lg font-semibold mb-3'>Primary Colors</h3>
            <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
              {colors.map(color => (
                <div key={color.name} className='space-y-2'>
                  <div
                    className='h-20 rounded-md shadow-sm border border-gray-200'
                    style={{ backgroundColor: `var(${color.cssVar})` }}
                  ></div>
                  <div className='text-sm'>
                    <div className='font-medium'>{color.name}</div>
                    <div className='text-gray-500 text-xs'>
                      var({color.cssVar})
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Semantic Colors */}
          <div>
            <h3 className='text-lg font-semibold mb-3'>Semantic Colors</h3>
            <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4'>
              {semanticColors.map(color => (
                <div key={color.name} className='space-y-2'>
                  <div
                    className='h-20 rounded-md shadow-sm'
                    style={{ backgroundColor: `var(${color.cssVar})` }}
                  ></div>
                  <div className='text-sm'>
                    <div className='font-medium'>{color.name}</div>
                    <div className='text-gray-500 text-xs'>
                      var({color.cssVar})
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gray Scale */}
          <div>
            <h3 className='text-lg font-semibold mb-3'>Gray Scale</h3>
            <div className='grid grid-cols-2 md:grid-cols-5 gap-4'>
              {grayScale.map(color => (
                <div key={color.name} className='space-y-2'>
                  <div
                    className='h-16 rounded-md shadow-sm border border-gray-200'
                    style={{ backgroundColor: `var(${color.cssVar})` }}
                  ></div>
                  <div className='text-sm'>
                    <div className='font-medium'>{color.name}</div>
                    <div className='text-gray-500 text-xs'>
                      var({color.cssVar})
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Spacing Scale */}
      <Card title='Spacing Scale' className='mb-4'>
        <div className='space-y-4'>
          {spacing.map(space => (
            <div key={space.name} className='flex items-center gap-4'>
              <div className='w-16 text-sm font-medium'>{space.name}</div>
              <div className={`${space.class} bg-blue-500`}></div>
              <div className='text-sm text-gray-500'>{space.value}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Border Radius */}
      <Card title='Border Radius' className='mb-4'>
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
          {borderRadius.map(radius => (
            <div key={radius.name} className='space-y-2'>
              <div
                className='bg-gray-200 h-20 flex items-center justify-center border border-gray-300'
                style={{ borderRadius: `var(${radius.cssVar})` }}
              >
                <span className='text-sm font-medium'>{radius.name}</span>
              </div>
              <div className='text-sm text-gray-500 text-center'>
                var({radius.cssVar})
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Shadows */}
      <Card title='Shadow Depths' className='mb-4'>
        <div className='grid grid-cols-2 md:grid-cols-4 gap-6'>
          {shadows.map(shadow => (
            <div key={shadow.name} className='space-y-2'>
              <div
                className={`${shadow.class} bg-white h-20 rounded-md flex items-center justify-center border border-gray-100`}
              >
                <span className='text-sm font-medium'>{shadow.name}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Icons */}
      <Card title='Common Icons (PrimeIcons)' className='mb-4'>
        <div className='grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12 gap-4'>
          {commonIcons.map(icon => (
            <div
              key={icon}
              className='flex flex-col items-center gap-2 p-2 hover:bg-gray-50 rounded'
            >
              <i className={`pi ${icon} text-2xl text-gray-700`}></i>
              <span className='text-xs text-gray-500 text-center'>
                {icon.replace('pi-', '')}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
